/*
 * O botão de emoji da barra do editor abre o seletor do tema.
 *
 * Com o flarum/emoji ligado, o botão dele (que só digita " :" e abre o
 * autocompletar) dá lugar ao do seletor, na mesma posição; o autocompletar
 * `:sor…` continua sendo o dele — o tema só o estiliza. Sem o flarum/emoji, o
 * tema mesmo oferece o autocompletar, com a mesma lista do seletor, buscando
 * em português e em inglês.
 *
 * O seletor insere o caractere do emoji, e não o atalho `:nome:`: é o que o
 * flarum/emoji também insere e o que ele converte em imagem nos posts — o
 * formatador dele não conhece os atalhos por nome.
 */
import { extend } from 'flarum/common/extend';
import TextEditor from 'flarum/common/components/TextEditor';
import TextEditorButton from 'flarum/common/components/TextEditorButton';
import Autocomplete from '../Autocomplete';
import { composerOn, editorDriver, extendToolbarLate } from '../common';
import { openEmojiPicker, preloadEmojiPicker } from './pickerLazy';
import { loadEmojiData, matches, normalize, twemojiUrl, usesTwemoji } from './emojiUtils';
import { isExtensionEnabled, t } from '../../../utils/settings';
const AUTOCOMPLETE_LIMIT = 12;
export default function installEmoji() {
  extendToolbarLate(function (items) {
    if (!composerOn('Emoji')) return;
    const priority = items.has('emoji') ? items.getPriority('emoji') : 0;
    items.add('emoji', m(TextEditorButton, {
      icon: "far fa-face-smile",
      className: "Button Button--icon Button--link CanelaEmojiButton",
      onmouseenter: preloadEmojiPicker,
      onfocus: preloadEmojiPicker,
      onclick: e => {
        const driver = editorDriver(this);
        openEmojiPicker(e.currentTarget, emoji => driver?.insertAtCursor(emoji, false));
      }
    }, t('composer.emoji.button')), priority);
  });

  // O autocompletar próprio só existe sem o flarum/emoji: dois menus no
  // mesmo ":" brigariam pelas setas e pelo Enter.
  extend(TextEditor.prototype, 'buildEditorParams', function (params) {
    if (!composerOn('Emoji') || isExtensionEnabled('flarum-emoji')) return;
    const autocomplete = new Autocomplete(this, {
      className: 'CanelaAutocomplete--emoji',
      trigger: ':',
      validBit: /[a-z0-9_+\-À-ɏ]/i,
      lookBehind: 20,
      header: () => t('composer.emoji.autocomplete_header'),
      suggest: typed => {
        // Só depois de duas letras, senão ":" num horário
        // ("10:30") ou num emoticon já abriria o menu.
        if (typed.length < 2) return [];
        return loadEmojiData().then(groups => {
          const terms = [normalize(typed)];
          const found = [];

          // Primeiro os que começam pelo atalho, depois qualquer palavra.
          for (const pass of [row => row[1].split(' ').some(code => code.startsWith(terms[0])), row => matches(row, terms)]) {
            for (const group of groups) {
              for (const row of group.emoji) {
                if (found.length >= AUTOCOMPLETE_LIMIT) break;
                if (!found.includes(row) && pass(row)) found.push(row);
              }
            }
          }
          return found.map(row => ({
            key: row[0],
            replacement: row[0],
            label: suggestionLabel(row)
          }));
        });
      }
    });
    this.canelaEmojiAutocomplete = autocomplete;
    params.inputListeners.push(autocomplete.listener);
  });
  extend(TextEditor.prototype, 'onbuild', function () {
    this.canelaEmojiAutocomplete?.attach();
  });
}
function suggestionLabel(row) {
  const code = row[1].split(' ')[0];
  return m('[', null, usesTwemoji() ? m("img", {
    className: "emoji",
    src: twemojiUrl(row[0]),
    alt: row[0],
    loading: "lazy"
  }) : m("span", {
    className: "CanelaAutocomplete-emoji"
  }, row[0]), m("span", {
    className: "CanelaAutocomplete-label"
  }, ":", code, ":"));
}
flarum.reg.add('ramon-canela', 'forum/features/composer/emoji/installEmoji', installEmoji);